import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { MapPin, ExternalLink, Calendar, Clock, ChevronDown, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const LocationHistory = ({ historicalData, isLoading, onLoadMore, hasMore, isLoadingMore = false, selectedTimeRange, onTimeRangeChange }) => {
  // Frontend pagination state'i
  const [displayedCount, setDisplayedCount] = useState(10); // İlk 10 kayıt göster
  const tableEndRef = useRef(null);

  // Yeni veri geldiğinde pagination'ı resetle
  useEffect(() => {
    setDisplayedCount(10);
  }, [historicalData.length]);
  // Tarih formatla - memoized performance için
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Timestamp zaten ms cinsinden olabilir ya da saniye cinsinden
    const ts = typeof timestamp === 'number' ? timestamp : parseInt(timestamp);
    const ms = ts < 1e12 ? ts * 1000 : ts; // Eğer saniye ise milisaniyeye çevir
    
    const date = new Date(ms);
    return isNaN(date) ? 'N/A' : date.toLocaleString('tr-TR');
  }, []);

  // Google Maps linki oluştur - memoized
  const getGoogleMapsLink = useCallback((latitude, longitude) => {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }, []);

  // Timestamp'i ms'e çevir - optimized
  const toMs = useCallback((ts) => {
    if (!ts) return null;
    if (typeof ts === 'string') {
      const d = new Date(ts);
      return isNaN(d) ? null : d.getTime();
    }
    if (typeof ts === 'number') {
      return ts < 1e12 ? ts * 1000 : ts;
    }
    return null;
  }, []);

  // Verileri memoize et
  const processedData = useMemo(() => {
    if (!historicalData || historicalData.length === 0) return [];

    const normalizeEntry = (entry) => {
      // Destek 1: Dashboard'dan gelen normalize edilmiş şekil
      if (entry && (entry.latitude != null) && (entry.longitude != null)) {
        const lat = Number(entry.latitude);
        const lon = Number(entry.longitude);
        if (isNaN(lat) || isNaN(lon)) return null;
        const tsMs = toMs(entry.timestamp);
        return {
          id: entry.id,
          latitude: lat.toFixed(6),
          longitude: lon.toFixed(6),
          altitude: (entry.altitude != null && !isNaN(Number(entry.altitude))) ? Number(entry.altitude).toFixed(1) : 'N/A',
          timestamp: entry.timestamp,
          formattedDate: tsMs ? new Date(tsMs).toLocaleString('tr-TR') : 'N/A',
          tsMs,
          googleMapsLink: getGoogleMapsLink(lat, lon)
        };
      }

      // Destek 2: Firebase'den gelen ham kayıt (gateway_data.gnss altında)
      const gnss = entry?.gateway_data?.gnss;
      if (!gnss) return null;
      const latRaw = gnss.latitude ?? gnss.lat;
      const lonRaw = gnss.longitude ?? gnss.lng ?? gnss.lon;
      if (latRaw == null || lonRaw == null) return null;
      const lat = Number(latRaw);
      const lon = Number(lonRaw);
      if (isNaN(lat) || isNaN(lon)) return null;
      const ts = gnss.timestamp ?? gnss.time ?? gnss.last_updated;
      const tsMs = toMs(ts);
      return {
        id: entry.id,
        latitude: lat.toFixed(6),
        longitude: lon.toFixed(6),
        altitude: (gnss.altitude != null && !isNaN(Number(gnss.altitude))) ? Number(gnss.altitude).toFixed(1) : (gnss.alt != null && !isNaN(Number(gnss.alt)) ? Number(gnss.alt).toFixed(1) : 'N/A'),
        timestamp: ts,
        formattedDate: tsMs ? new Date(tsMs).toLocaleString('tr-TR') : 'N/A',
        tsMs,
        googleMapsLink: getGoogleMapsLink(lat, lon)
      };
    };

    return historicalData
      .map(normalizeEntry)
      .filter(Boolean)
      .sort((a, b) => (b.tsMs ?? 0) - (a.tsMs ?? 0));
      // slice kaldırıldı - tüm verileri göster
  }, [historicalData]);

  // Frontend pagination - sadece belirli sayıda göster
  const displayedData = useMemo(() => {
    return processedData.slice(0, displayedCount);
  }, [processedData, displayedCount]);

  // Excel Export fonksiyonu
  const exportToExcel = useCallback(() => {
    if (!processedData.length) return;

    // Excel için veri hazırla - processedData zaten normalize edilmiş
    const excelData = processedData.map((entry, index) => {
      // processedData'da veri zaten normalize edilmiş: {latitude, longitude, altitude, timestamp}
      const lat = entry.latitude ? Number(entry.latitude) : null;
      const lng = entry.longitude ? Number(entry.longitude) : null;
      const alt = entry.altitude !== 'N/A' ? Number(entry.altitude) : null;
      
      return {
        'Sıra': index + 1,
        'Tarih Saat': entry.formattedDate || formatDate(entry.timestamp),
        'Enlem': lat ? lat.toFixed(6) : 'N/A',
        'Boylam': lng ? lng.toFixed(6) : 'N/A',
        'Yükseklik': alt ? `${alt.toFixed(1)} m` : 'N/A',
        'Google Maps': entry.googleMapsLink || getGoogleMapsLink(lat, lng)
      };
    });

    // Worksheet oluştur
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Column genişlikleri ayarla
    ws['!cols'] = [
      { width: 8 },  // Sıra
      { width: 20 }, // Tarih Saat
      { width: 12 }, // Enlem
      { width: 12 }, // Boylam
      { width: 12 }, // Yükseklik
      { width: 50 }  // Google Maps
    ];

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Konum Geçmişi');

    // Dosya adı (tarih ile)
    const now = new Date();
    const fileName = `Konum_Gecmisi_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}_${now.getHours().toString().padStart(2,'0')}-${now.getMinutes().toString().padStart(2,'0')}.xlsx`;

    // Dosyayı indir
    XLSX.writeFile(wb, fileName);
  }, [processedData, formatDate, getGoogleMapsLink]);

  // Intersection Observer ile otomatik yükleme
  useEffect(() => {
    if (!onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          console.log('🔄 Otomatik yükleme tetiklendi');
          onLoadMore();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // 50px önce tetikle
      }
    );

    if (tableEndRef.current) {
      observer.observe(tableEndRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [onLoadMore, isLoadingMore, hasMore]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-600">Konum verileri yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Konum Verisi Bulunamadı</h3>
          <p className="text-gray-600">
            Seçili zaman aralığında konum verisi bulunamadı. 
            Farklı bir zaman aralığı seçmeyi deneyin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <MapPin className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Konum Geçmişi</h3>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => onTimeRangeChange && onTimeRangeChange(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="1h">Son 1 Saat</option>
            <option value="6h">Son 6 Saat</option>
            <option value="24h">Son 24 Saat</option>
            <option value="7d">Son 7 Gün</option>
            <option value="30d">Son 30 Gün</option>
          </select>
          <div className="text-sm text-gray-500">
            {displayedData.length} kayıt gösteriliyor
            {hasMore && <span className="ml-2 text-blue-600">(Daha fazla mevcut)</span>}
          </div>
          <button
            onClick={exportToExcel}
            disabled={!processedData.length}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download className="h-3 w-3 mr-1" />
            Excel ({processedData.length})
          </button>
        </div>
      </div>

      {/* Özet Bilgiler - Tablonun üstünde */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm font-medium text-blue-600">Bu Zaman Aralığında</div>
          <div className="text-2xl font-bold text-blue-900">
            {processedData.length}+ Konum
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Gösterilen: {displayedData.length} {hasMore && '(Daha fazla var)'}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm font-medium text-green-600">İlk Kayıt</div>
          <div className="text-sm font-semibold text-green-900">
            {processedData.length > 0 ? formatDate(processedData[processedData.length - 1].timestamp) : 'N/A'}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm font-medium text-purple-600">Son Kayıt</div>
          <div className="text-sm font-semibold text-purple-900">
            {processedData.length > 0 ? formatDate(processedData[0].timestamp) : 'N/A'}
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-sm font-medium text-orange-600">Ortalama Yükseklik</div>
          <div className="text-sm font-semibold text-orange-900">
            {(() => {
              const validAltitudes = displayedData
                .map(item => item.altitude)
                .filter(alt => alt !== 'N/A')
                .map(alt => parseFloat(alt));
              
              if (validAltitudes.length === 0) return 'N/A';
              
              const avg = validAltitudes.reduce((sum, alt) => sum + alt, 0) / validAltitudes.length;
              return `${avg.toFixed(1)} m`;
            })()}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Tarih & Saat
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Enlem
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Boylam
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Yükseklik
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Harita
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedData.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                    {item.formattedDate}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-600">
                  {item.latitude}°
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-green-600">
                  {item.longitude}°
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.altitude === 'N/A' ? 'N/A' : `${item.altitude} m`}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <a
                    href={item.googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Haritada Aç
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Backend Pagination Controls */}
      {(() => {
        const hasMoreFromBackend = hasMore; // Backend'den daha veri var mı?
        const hasMoreFromFrontend = processedData.length > displayedData.length; // Frontend'de gizli veri var mı?
        
        console.log('🔧 Pagination debug:', {
          processedDataLength: processedData.length,
          displayedDataLength: displayedData.length,
          hasMoreFromFrontend,
          hasMoreFromBackend,
          displayedCount
        });
        
        // Önce frontend'deki gizli verileri göster, sonra backend'den yeni veri çek
        if (hasMoreFromFrontend) {
          return (
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  console.log('📄 Frontend pagination: +10 kayıt daha göster');
                  setDisplayedCount(prev => prev + 10);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ChevronDown className="h-4 w-4 mr-2" />
                10 Kayıt Daha Göster ({processedData.length - displayedData.length} kaldı)
              </button>
            </div>
          );
        } else if (hasMoreFromBackend && onLoadMore) {
          return (
            <div className="mt-6 text-center">
              {isLoadingMore ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-gray-600">Sonraki 10 kayıt yükleniyor...</span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    console.log('📄 Backend pagination: Backend\'den +10 kayıt çek');
                    onLoadMore();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Backend'den 10 Kayıt Daha Yükle
                </button>
              )}
            </div>
          );
        }
        
        return null;
      })()}

      {/* Intersection Observer Target */}
      <div ref={tableEndRef} className="h-4" />
    </div>
  );
};

export default LocationHistory;
